import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { format } from "date-fns";
import { useIsMobile } from "@/hooks/use-mobile";
import type { PersonnelProject, RateSchedule, TMWorker } from "@/hooks/usePersonnelProjects";

interface Props {
  project: PersonnelProject;
  workers: TMWorker[];
  rates: RateSchedule[];
}

export function PersonnelWorkersTab({ project, workers, rates }: Props) {
  const isMobile = useIsMobile();

  return (
    <div className="space-y-4 mt-4">
      <h2 className="text-base font-semibold">Workers ({workers.length})</h2>
      {workers.length === 0 ? (
        <p className="text-center py-8 text-muted-foreground">No workers assigned</p>
      ) : isMobile ? (
        <div className="space-y-3">
          {workers.map((w) => {
            const rate = rates.find((r) => r.id === w.rate_schedule_id);
            return (
              <Card key={w.id}>
                <CardContent className="p-4 space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{w.worker_name}</span>
                    <Badge variant="outline" className={w.is_active ? "bg-emerald-500/10 text-emerald-700" : "bg-muted text-muted-foreground"}>
                      {w.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>{rate?.role_name ?? "—"}</span>
                    <span>${rate?.bill_rate_regular ?? 0}/HR</span>
                  </div>
                  {w.start_date && (
                    <p className="text-xs text-muted-foreground">Started {format(new Date(w.start_date), "MMM d")}</p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Rate</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Start</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {workers.map((w) => {
                  const rate = rates.find((r) => r.id === w.rate_schedule_id);
                  return (
                    <TableRow key={w.id}>
                      <TableCell className="font-medium">{w.worker_name}</TableCell>
                      <TableCell>{rate?.role_name ?? "—"}</TableCell>
                      <TableCell>${rate?.bill_rate_regular ?? 0}/HR</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={w.is_active ? "bg-emerald-500/10 text-emerald-700" : "bg-muted text-muted-foreground"}>
                          {w.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell>{w.start_date ? format(new Date(w.start_date), "MMM d") : "—"}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
